import type { SessionUserDto } from '@ukinfo/shared';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from './api';
import { isIos, isStandalone } from './push';

/**
 * Angemeldet auf den Home-Bildschirm.
 *
 * Die App auf dem iPhone hat einen eigenen Speicher. Safari uebernimmt beim
 * Ablegen aber die aktuelle Adresse - deshalb steht dort, solange man in
 * Safari angemeldet ist, ein einmaliger Code (?app=...). Die App loest ihn
 * beim ersten Start ein. Im gewoehnlichen Browser wird er nie eingeloest,
 * sondern nur aus der Adresse entfernt: wer eine Adresse weitergibt, gibt
 * damit keine Anmeldung weiter.
 */
const PARAM = 'app';
/** Rechtzeitig vor Ablauf (15 Minuten) erneuern. */
const REFRESH_MS = 5 * 60_000;

let current: { code: string; expiresAt: number } | null = null;

/** Die persoenliche Rueckmeldeseite hat ihren eigenen Code (siehe TrainingInvitePage). */
function isLinkPage(pathname: string): boolean {
  return pathname === '/antworten' || pathname.startsWith('/t/');
}

function setParam(code: string | null): void {
  const url = new URL(window.location.href);
  if (code) url.searchParams.set(PARAM, code);
  else url.searchParams.delete(PARAM);
  // Den Zustand des Routers mitnehmen, sonst stimmt "Zurueck" nicht mehr.
  window.history.replaceState(window.history.state, '', url);
}

/**
 * Beim Start: Code in der Adresse? In der App einloesen, sonst nur entfernen.
 * Liefert den Benutzer, wenn die App dadurch angemeldet wurde.
 */
export async function redeemFromUrl(): Promise<SessionUserDto | null> {
  if (isLinkPage(window.location.pathname)) return null;
  const code = new URLSearchParams(window.location.search).get(PARAM);
  if (!code) return null;
  setParam(null);
  if (!isStandalone()) return null;
  try {
    return await api.post<SessionUserDto>('/api/auth/app-transfer/redeem', { code });
  } catch {
    return null;
  }
}

/** Haelt in Safari auf dem iPhone einen gueltigen Code in der Adresse. */
export function useAppStartCode(user: SessionUserDto | null): void {
  const location = useLocation();
  const active = Boolean(user) && !user?.isImpersonating && isIos() && !isStandalone() && !isLinkPage(location.pathname);

  useEffect(() => {
    if (!active) return;
    let stopped = false;
    const apply = async (): Promise<void> => {
      if (!current || current.expiresAt - Date.now() < REFRESH_MS) {
        const next = await api.post<{ code: string; expiresAt: string }>('/api/auth/app-transfer');
        current = { code: next.code, expiresAt: Date.parse(next.expiresAt) };
      }
      if (!stopped) setParam(current.code);
    };
    void apply().catch(() => undefined);
    const timer = window.setInterval(() => void apply().catch(() => undefined), REFRESH_MS);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
    // Nach jedem Seitenwechsel neu setzen - der Router schreibt die Adresse ohne Code.
  }, [active, location.pathname, location.search]);
}
